// mediationHealth.js — WP4c client (docs/frontend-validation-port-spec.md §4
// WP4c). Posts rows plus the caller's declared-adapter lists to the proxy's
// buildMediationHealth / buildSecureSignalsHealth ports and returns the
// server-computed rollups that InspectraNetworkView.jsx's
// AdServerIntegrationCard / lifecycle view used to compute locally via
// validation/mediationHealth.js and validation/secureSignalsHealth.js.
//
// WHY declaredAdapters/declaredSecureSignals TRAVEL IN THE BODY, NOT AN
// appId: the spec's WP4c line guesses these come from an app-pack selection
// the server could resolve itself from `appId` via proxy-server/appPacks.js.
// Verified false against the running tree before building on it:
//   - proxy-server/appPacks.js's pack shape carries no adapter/secure-signal
//     roster anywhere (identity, iuGrammar, taxonomy, stack{hasPrebid,...},
//     contentApis, authSignal, custParamRules, sslExcludedDomains, policy) —
//     confirmed by reading the full module and grepping the tree for
//     "appPack"/"appId" near "adapter"/"secureSignal": no hits.
//   - Both lists are the `adapters` / `secureSignalAdapters` fields of a
//     live-captured `publisher_settings` request's decoded body (the GMA SDK
//     Publisher Settings response), computed server-side already by
//     proxy-server/utils.js's own publisher_settings validator — but that
//     snapshot is held in the BROWSER's own state
//     (context/TrafficContext.js's `pubSettings`, "latest wins") because the
//     source row's `validData` gets stripped once it ages past the two-tier
//     full-body retention window. It cannot be reliably re-derived from
//     `rows` alone, and it is not server-owned config.
// So this is the WP4a seam (`rules` for custParamsRulesCheck), not an
// app-pack lookup: the browser resolves its own publisher_settings snapshot
// and passes the two lists in as plain request parameters. The server never
// sources them itself.
//
// Mirrors api/custParamsRules.js's own request()/PROXY_API_BASE pattern
// rather than importing it — house convention duplicates this per-file
// rather than sharing a helper module.
//
// Route contract (server side owned by proxy-server/apiServer.js, not this
// file):
//   POST /proxy/validation/mediation-health
//   body: {
//     rows: MediationHealthRow[],
//     declaredAdapters: Array<{name:string}|string>,      // Publisher
//       Settings `adapters` (or a plain name list) — see buildMediationHealth
//     declaredSecureSignals: string[],                    // Publisher
//       Settings `secureSignalAdapters`, already filtered by the caller to
//       the a3p-trackable set (UID2/LiveRamp) — see buildSecureSignalsHealth
//   }
//   response: {
//     mediationHealth: ReturnType<buildMediationHealth> | null,
//     secureSignalsHealth: ReturnType<buildSecureSignalsHealth> | null,
//   }
//   A single combined route rather than two: both rollups are always read
//   together off the same rows/publisher_settings snapshot in
//   InspectraNetworkView.jsx (AdServerIntegrationCard + the lifecycle view's
//   secure-signals panel), so one POST avoids a second full row payload for
//   data the caller already sent once (spec §5 recipe #4's payload-size
//   concern) and a second inlined debounce timer (spec §4 WP1's "second
//   inlined 400ms debounce literal" trap).

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

async function request(path, init = {}) {
  let res;
  try {
    res = await fetch(`${PROXY_API_BASE}/proxy${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(init.headers || {}) },
    });
  } catch (e) {
    const err = new Error(`Could not reach the proxy server at ${PROXY_API_BASE}`);
    err.status = 0;
    err.body = null;
    err.cause = e;
    throw err;
  }
  let json = null;
  try { json = await res.json(); } catch { /* empty/non-JSON body */ }
  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `${init.method || 'GET'} ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

// The only fields proxy-server/validation/mediationHealth.js and
// proxy-server/validation/secureSignalsHealth.js read off a row: `requestType`
// / `type` (classification), `validData` (networkId, networkName, kind,
// outcome, placementId, messages, secureSignals — read wholesale, same as
// api/lifecycle.js's LIFECYCLE_ROW_FIELDS treats it), `url` and `_k`/`id`
// (secureSignalsHealth's reqLabel() drill-in text for the `missing` list).
// Deliberately its own whitelist, NOT LIFECYCLE_ROW_FIELDS — this route does
// not live under proxy-server/lifecycle/ (WP4c's brief excludes touching
// that directory), so it gets its own projection and its own guard test
// (api/mediationHealthRowFields.test.js), matching the precedent set by
// api/disclosures.js for the same reason.
export const MEDIATION_ROW_FIELDS = ['_k', 'id', 'url', 'type', 'requestType', 'validData'];

export function projectRowForMediationHealth(row) {
  if (!row || typeof row !== 'object') return row;
  const out = {};
  MEDIATION_ROW_FIELDS.forEach((f) => {
    if (f in row) out[f] = row[f];
  });
  return out;
}

/**
 * postMediationHealth(rows, declaredAdapters, declaredSecureSignals) ->
 *   Promise<{ mediationHealth, secureSignalsHealth }>
 *
 * rows: the caller's full (unprojected) row array; only the whitelisted
 * fields are sent over the wire.
 * declaredAdapters: Publisher Settings `adapters` — array of {name,...} or
 * plain name strings (buildMediationHealth accepts either). May be empty.
 * declaredSecureSignals: Publisher Settings `secureSignalAdapters`, already
 * filtered by the caller to the a3p-trackable set. May be empty.
 *
 * Throws on transport failure or non-2xx response, same contract as
 * postDisclosures/postCustParamsRules — callers that fall back to the local
 * frontend computation on failure must do so explicitly, not silently.
 */
export async function postMediationHealth(rows, declaredAdapters, declaredSecureSignals) {
  const projected = (rows || []).map(projectRowForMediationHealth);
  return request('/validation/mediation-health', {
    method: 'POST',
    body: JSON.stringify({
      rows: projected,
      declaredAdapters: Array.isArray(declaredAdapters) ? declaredAdapters : [],
      declaredSecureSignals: Array.isArray(declaredSecureSignals) ? declaredSecureSignals : [],
    }),
  });
}
